import * as React from 'react';
import { Button, Chip, FormSelect } from 'ui';
import { Grid } from '@mui/material';
import { Theme, css } from '@emotion/react';
import { tocktix } from '@tock/protos/dist/types/clowncar';
import { uniqueId } from 'es-toolkit/compat';

interface MetaTagSelectorProps {
  name: string;
  label: string;
  pendingTagName: string;
  selectedTags: tocktix.messages.IMetaTag[];
  tagMap: Map<string, tocktix.messages.IMetaTag>;
  addSelectedTag: (tag: tocktix.messages.IMetaTag) => void;
  clearSelectedTags: () => void;
  description: string;
  defaultOptionLabel?: string;
}

const metaTagSelectorSelect = css`
  width: 60%;
`;

const metaTagSelectorContainer = ({ spacing }: Theme) => css`
  display: flex;
  justify-content: space-between;
  padding-right: ${spacing(2)};
`;

const MetaTagSelector: React.FunctionComponent<React.PropsWithChildren<MetaTagSelectorProps>> = ({
  name,
  label,
  description,
  pendingTagName,
  selectedTags,
  addSelectedTag,
  clearSelectedTags,
  tagMap,
  defaultOptionLabel = '[select tags here]',
}) => {
  // convert tag data into form select options
  const options = React.useMemo(() => {
    const opts: React.ReactNode[] = [];

    tagMap.forEach((_value, key) => {
      opts.push(
        <option key={uniqueId()} value={key}>
          {key}
        </option>
      );
    });

    return opts;
  }, [tagMap]);

  const chips = React.useMemo(() => {
    return selectedTags.map((tag: tocktix.messages.IMetaTag) => (
      <Chip key={tag.id} color="success" label={tag.name} />
    ));
  }, [selectedTags]);

  return (
    <Grid item container xs={12}>
      <Grid item xs={8} css={metaTagSelectorContainer}>
        <FormSelect
          name={name}
          inputProps={{
            name: name,
            label: label,
          }}
          disabled={options.length === 0}
          css={metaTagSelectorSelect}
        >
          <option key="blank" value="">
            {defaultOptionLabel}
          </option>
          {options}
        </FormSelect>
        <Button
          disabled={pendingTagName === ''}
          onClick={() => {
            const tag = tagMap.get(pendingTagName);
            if (tag) {
              addSelectedTag(tag);
            }
          }}
        >
          Add tag
        </Button>

        <Button disabled={selectedTags.length === 0} onClick={clearSelectedTags}>
          Clear tags
        </Button>
      </Grid>
      <Grid item xs={4}>
        Tags: {chips}
      </Grid>
      <div>{description}</div>
    </Grid>
  );
};

export default MetaTagSelector;
